import { formatDistanceToNow } from 'date-fns';
import {
  Badge,
  Box,
  Flex,
  Pagination,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@optiaxiom/react';

import { EmptyPage } from '@frontend/components';
import { AgentExecutionItem, ExecutionStatus } from '@frontend/types/agentExecution';

import { paginationWrapper, runningRowStyle } from './ExecutionsTable.css';

type ExecutionsTableProps = {
  executions: AgentExecutionItem[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  isLoading: boolean;
  error: Error | null;
  onExecutionSelect: (execution: AgentExecutionItem) => void;
  selectedExecutionId?: string;
  onPageChange: (page: number) => void;
  onWorkflowExecutionView?: (execution: AgentExecutionItem) => void;
};

const getStatusBadgeIntent = (status: ExecutionStatus) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
    case 'pending': // Treat pending as running in UI
      return 'warning';
    default:
      return 'neutral';
  }
};

const formatTimestamp = (timestamp: string) => {
  // Ensure timestamp is treated as UTC by appending 'Z' if not present
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);

  // Return relative time (e.g., "2 minutes ago")
  return formatDistanceToNow(date, { addSuffix: true });
};

const formatFullTimestamp = (timestamp: string) => {
  // Ensure timestamp is treated as UTC by appending 'Z' if not present
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);

  // Format as local date and time
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

const getTriggerType = (execution: AgentExecutionItem): string => {
  // Get trigger type from trigger_info if available
  if (execution.trigger_info?.type) {
    return (
      execution.trigger_info.type.charAt(0).toUpperCase() + execution.trigger_info.type.slice(1)
    );
  }
  return execution.trigger_type || 'Manual';
};

const getRowClassName = (status: ExecutionStatus): string => {
  switch (status.toLowerCase()) {
    case 'pending': // Treat pending as running
    case 'running':
      return runningRowStyle;
    default:
      return '';
  }
};

const getRowInlineStyle = (status: ExecutionStatus, isSelected: boolean) => {
  const baseStyle = {
    cursor: 'pointer' as const,
    backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : undefined,
  };

  // Add visible styles for running executions (including pending)
  switch (status.toLowerCase()) {
    case 'pending': // Treat pending as running
    case 'running':
      return {
        ...baseStyle,
        backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : 'rgba(245, 158, 11, 0.1)',
        background: isSelected
          ? 'var(--colors-bg-secondary)'
          : 'linear-gradient(90deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.3) 50%, rgba(245, 158, 11, 0.05) 100%)',
        backgroundSize: '200% 100%',
        animation: 'gradient-shimmer 1.5s ease-in-out infinite',
      };
    default:
      // Debug: add a light background to all rows to test styling
      return {
        ...baseStyle,
        backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : 'rgba(0, 0, 0, 0.02)',
      };
  }
};

const getTriggerName = (execution: AgentExecutionItem): string => {
  // Get trigger name from trigger_info if available
  const triggerName = execution.trigger_info?.payload?.trigger_name as string;
  if (triggerName) {
    return triggerName;
  }
  // Fallback to type-based name
  const type = getTriggerType(execution);
  return `${type} Trigger`;
};

const isWorkflowExecution = (execution: AgentExecutionItem): boolean => {
  // Check if this execution is from a workflow agent
  // We can determine this by checking if workflow_agent data exists
  return !!execution.workflow_agent;
};

export function ExecutionsTable({
  executions,
  pagination,
  isLoading,
  error,
  onExecutionSelect,
  selectedExecutionId,
  onPageChange,
  onWorkflowExecutionView,
}: ExecutionsTableProps) {
  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" h="224">
        <Spinner size="lg">Loading executions...</Spinner>
      </Flex>
    );
  }

  if (error) {
    return <EmptyPage title="Failed to load executions" description={error.message} />;
  }

  if (executions.length === 0) {
    return (
      <EmptyPage
        title="No executions found"
        description="Agent executions will appear here as they are performed."
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="16">
      <style>{`
        @keyframes gradient-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Timestamp</TableHeaderCell>
            <TableHeaderCell>Trigger Type</TableHeaderCell>
            <TableHeaderCell>Trigger Name</TableHeaderCell>
            <TableHeaderCell>Agent Name</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {executions.map(execution => {
            const handleRowClick = () => {
              if (isWorkflowExecution(execution) && onWorkflowExecutionView) {
                onWorkflowExecutionView(execution);
              } else {
                onExecutionSelect(execution);
              }
            };

            return (
              <TableRow
                key={execution.id}
                onClick={handleRowClick}
                className={getRowClassName(execution.status)}
                style={getRowInlineStyle(execution.status, selectedExecutionId === execution.id)}
                data-status={execution.status}
              >
                <TableCell>
                  <Text fontSize="md" title={formatFullTimestamp(execution.start_time)}>
                    {formatTimestamp(execution.start_time)}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text fontSize="md">{getTriggerType(execution)}</Text>
                </TableCell>
                <TableCell>
                  <Text fontSize="md">{getTriggerName(execution)}</Text>
                </TableCell>
                <TableCell>
                  <Text fontSize="md" color="fg.tertiary">
                    {execution.workflow_agent?.name || 'Unknown Agent'}
                  </Text>
                </TableCell>
                <TableCell>
                  <Badge intent={getStatusBadgeIntent(execution.status)}>
                    {execution.status.toLowerCase() === 'pending' ? 'Running' : execution.status}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && pagination.total_pages > 0 && (
        <Flex justifyContent="center" alignItems="center" flexDirection="column" gap="8" mt="16">
          <Text fontSize="sm" color="fg.secondary">
            Showing {executions.length} of {pagination.total_count} executions
          </Text>
          <Box className={paginationWrapper}>
            <Pagination
              page={pagination.page}
              total={pagination.total_pages}
              onPageChange={onPageChange}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
